/**
 * LINE の custom_token をガードより先に消費する。
 * メール変更の再認証中は既ログインでも signInWithCustomToken し、basicinfo へ戻す。
 */
import { getFirebaseAuth, signInWithCustomToken } from '@/infra/firebase/auth'
import {
  clearEmailChangePending,
  readEmailChangePending,
  writeEmailChangeAccountMismatch
} from '@/utils/auth/emailChangePending'

export type ConsumeLineCustomTokenInput = {
  isLoggedIn: boolean
  path: string
  customToken: unknown
  /** ネイティブアプリからの `redirect_path` クエリ（未検証の生値）。 */
  redirectPath?: unknown
  hintAuthMethod: (method: string) => void
  clearAuthMethodHint: () => void
}

/**
 * ネイティブアプリが custom_token 経由で送れる内部パス。
 *
 * アプリはカスタムトークンの受領口である /mypage で認証してから目的ページへ
 * 移る。任意のパスを許すとオープンリダイレクトになるため、固定の許可リストで
 * 受ける。
 */
const APP_REDIRECT_ALLOWLIST = [
  // 「他のテンプレートはこちら」
  '/what',
  // 「応募先にメール」→ ヤギのメール便の入口
  '/yagiprimequickemail/basic'
]

export type ConsumeLineCustomTokenResult = {
  isLoggedIn: boolean
  redirectPath?: string
  uid?: string
}

function tokenString(customToken: unknown): string {
  if (Array.isArray(customToken)) return String(customToken[0] || '')
  return String(customToken || '')
}

/** 許可リストに載っているパスだけを返す。それ以外は無視する。 */
function allowedAppRedirect(redirectPath: unknown): string | undefined {
  const path = Array.isArray(redirectPath)
    ? String(redirectPath[0] || '')
    : String(redirectPath || '')
  return APP_REDIRECT_ALLOWLIST.includes(path) ? path : undefined
}

export async function consumeLineCustomToken(
  input: ConsumeLineCustomTokenInput
): Promise<ConsumeLineCustomTokenResult> {
  const token = tokenString(input.customToken)
  if (input.path !== '/mypage' || !token) {
    return { isLoggedIn: input.isLoggedIn }
  }

  const appRedirect = allowedAppRedirect(input.redirectPath)
  const pending = readEmailChangePending()
  // 既ログインでもトークンを消費するのは、メール変更の再認証と、
  // ネイティブアプリからの遷移（Web側のログイン状態が別なことがある）だけ。
  if (input.isLoggedIn && !pending && !appRedirect) {
    return { isLoggedIn: input.isLoggedIn }
  }

  try {
    input.hintAuthMethod('line')
    const credential = await signInWithCustomToken(getFirebaseAuth(), token)
    const signedIn = !!(credential && credential.user)
    if (!signedIn) {
      return { isLoggedIn: false }
    }

    if (pending) {
      if (credential.user.uid !== pending.uid) {
        clearEmailChangePending()
        writeEmailChangeAccountMismatch()
      }
      return {
        isLoggedIn: true,
        redirectPath: '/mypage/basicinfo',
        uid: credential.user.uid
      }
    }

    return {
      isLoggedIn: true,
      redirectPath: appRedirect,
      uid: credential.user.uid
    }
  } catch (err) {
    console.error('signInWithCustomToken failed in router guard:', err)
    input.clearAuthMethodHint()
    return { isLoggedIn: input.isLoggedIn }
  }
}
